/**
 * Inteligência de Custo › Internações: quanto custa uma internação INTEIRA em
 * cada rede, comparando a mesma cirurgia com a mesma cirurgia.
 *
 * A diária sozinha engana (cada rede põe coisas diferentes nela); a unidade
 * honesta é a conta inteira. Toda regra de montagem aparece na tela, e cada
 * número abre até o item da conta.
 * Backend: /resultado-plano/utilizacao/internacao/* (servico_internacao.py).
 */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, X, Copy } from "lucide-react";
import toast from "react-hot-toast";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip as RTooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";
import api from "../../services/api";
import InfoTip from "../../components/InfoTip";
import { SeletorLado } from "./RedePrecos";

const T = {
  ink: "#2c3e50", inkSoft: "#7f8c8d", line: "#e8e8e8", teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4",
  risco: "#e74c3c", ambar: "#f39c12", ambarSoft: "#fef5e7", azul: "#3498db", verde: "#27ae60",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";
const BASE = "/resultado-plano/utilizacao/internacao";
const CORES_NAT = { diaria: "#3498db", taxa: "#9b59b6", procedimento: "#20a490", material_medicamento: "#f39c12" };

const vazio = (v) => v === null || v === undefined || Number.isNaN(v);
const fmtR = (v) => (vazio(v) ? "—" : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtR0 = (v) => (vazio(v) ? "—" : `R$ ${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`);
const fmtN = (v, c = 0) => (vazio(v) ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: c, maximumFractionDigits: c }));
const fmtP = (v) => (vazio(v) ? "—" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const fmtK = (v) => (Math.abs(v) >= 1e6 ? `${fmtN(v / 1e6, 1)} mi` : Math.abs(v) >= 1e3 ? `${fmtN(v / 1e3, 0)} mil` : fmtN(v, 0));
const dataBR = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "—");
const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : "");
const dataHoraBR = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const erroDe = (e, p) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : p; };
const curto = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);

const TH = { padding: "7px 8px", fontWeight: 600, fontSize: 12, color: T.inkSoft, borderBottom: `1px solid ${T.line}`, textAlign: "left", position: "sticky", top: 0, background: "#fff" };
const TD = { padding: "6px 8px", fontSize: 13, color: T.ink, borderBottom: `1px solid ${T.line}` };
const NUM = { ...TD, fontFamily: MONO, textAlign: "right", whiteSpace: "nowrap" };
const card = { background: "#fff", border: `1px solid ${T.line}`, borderRadius: 12, padding: 18, marginTop: 14 };
const btn = (a) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${a ? T.teal : T.line}`, background: a ? T.tealSoft : "#fff", color: a ? T.teal : T.ink });
const campo = { fontSize: 12, padding: "5px 8px", border: `1px solid ${T.line}`, borderRadius: 6, background: "#fff" };
const titulo = { fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 6, display: "flex", gap: 6, alignItems: "center" };

const TIP = {
  tela: "Compara o custo da internação inteira (diárias, taxas, materiais, medicamentos, procedimentos e honorários) entre redes. A diária sozinha engana porque cada rede inclui coisas diferentes nela. A comparação justa é a mesma cirurgia contra a mesma cirurgia, pela mediana.",
  periodo: "Internações que COMEÇARAM no período (primeira data de atendimento da guia). A conta de uma internação chega ao longo de meses; por isso o período sugerido termina 3 meses antes da última competência carregada, para as contas estarem completas.",
  resumo: "Todas as internações do período, pela rede do hospital que cobrou a diária. Aqui ainda não é comparação justa: uma rede pode internar casos mais graves. Serve para saber o tamanho de cada lado e de que é feita a conta.",
  mediana: "Mediana é o valor do meio: metade das internações custou menos e metade custou mais. Diferente da média, um caso extremo (uma internação de R$ 300 mil) não a arrasta.",
  faixa: "Faixa do meio: 25% das internações custaram menos que o primeiro valor e 25% custaram mais que o segundo. Mostra o quanto o custo varia.",
  composicao: "De que é feita a conta, em % do valor: diárias; taxas e gases; procedimentos, honorários e exames; materiais, medicamentos e outros. Rede com materiais embutidos na diária aparece com mais diária e menos material.",
  comparativo: "Para cada cirurgia (procedimento principal) feita nos dois lados com o mínimo de casos, compara a mediana do custo da internação inteira. O total aplica, às internações do primeiro lado, a mediana do segundo lado da mesma cirurgia.",
  minimo: "Número mínimo de internações da mesma cirurgia em cada lado para entrar na comparação. Com poucos casos, a mediana muda muito de um caso para outro.",
  clinicas: "Internações sem procedimento cirúrgico (tratamento clínico). Como não há uma cirurgia que defina o caso, a comparação é pelo custo por diária do paciente (valor total ÷ diárias), que ainda depende da gravidade dos casos.",
  pendencia: "Conta possivelmente incompleta: existem itens da mesma pessoa, no mesmo hospital e nas mesmas datas (ou o mesmo procedimento principal por outro prestador) que chegaram fora da guia da internação, muitas vezes em conta ambulatorial. Eles NÃO entram nos valores da tela. Marcamos só quando somam pelo menos R$ 5 mil e 10% da internação. Pode ser ajuste de lançamento no operador ou uma regra de faturamento que ainda não aplicamos.",
  operador: "Os casos mais claros de conta possivelmente incompleta no período, para levar ao operador. Os valores da tela não mudam; estes casos explicam por que uma rede pode parecer mais barata do que é.",
  carga: "Como as internações foram montadas na última atualização: quantos itens chegaram sem número de guia, quantos foram ligados com segurança e quanto ficou de fora.",
  cobertura: "Parte do gasto hospitalar da rede no período que entrou em internações montadas. O restante chegou do ERP sem número de guia e sem como ligar com segurança a uma internação (parte do intercâmbio vem assim). Se um lado tem cobertura bem menor que o outro, a comparação fica desequilibrada: aquele lado parece mais barato do que é.",
};

const H = { fontSize: 13, fontWeight: 700, color: T.ink, margin: "14px 0 4px" };
const P = { fontSize: 13, color: T.ink, margin: "0 0 6px", lineHeight: 1.55 };
const LI = { fontSize: 13, color: T.ink, lineHeight: 1.55, marginBottom: 3 };

// ---------- passo a passo ----------
function Metodologia({ comp, carga, minCasos }) {
  const ex = comp?.comparaveis?.[0];
  const ra = comp?.rotulo_a || "o primeiro lado", rb = comp?.rotulo_b || "o segundo lado";
  return (
    <div style={{ marginTop: 12, padding: "6px 18px 14px", borderRadius: 10, border: `1px solid ${T.line}`, background: "#fcfdfd" }}>
      <div style={H}>1. Por que comparar a internação inteira, e não a diária</div>
      <p style={P}>
        Cada rede cobra a internação de um jeito. Uma cobra a diária e lança materiais e medicamentos item por item; outra embute parte disso na diária ou nas taxas.
        Comparar só a diária mede a forma de cobrar, não o custo. A conta inteira (tudo o que foi pago naquela internação) é a mesma coisa nas duas redes.
      </p>

      <div style={H}>2. Como uma internação é montada</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}><b>Internação = uma guia</b> de conta hospitalar no ERP que tenha pelo menos uma diária. Conferimos nos dados: cada guia é de uma pessoa e de um hospital. Guia sem diária (por exemplo, um exame lançado em conta hospitalar) não é internação; guia com mais de uma pessoa fica fora. As duas situações são contadas no quadro "Como as internações foram montadas".</li>
        <li style={LI}><b>Hospital = quem cobra a diária.</b> A rede da internação é a rede desse hospital, pela mesma regra de classificação da aba Rede e preços.</li>
        <li style={LI}><b>Itens sem número de guia:</b> parte da conta (principalmente do intercâmbio) chega sem o número da guia. Esses itens entram <b>só</b> quando batem a mesma pessoa, o mesmo prestador e uma data dentro do período de <b>exatamente uma</b> internação. Se couberem em duas, ou em nenhuma, ficam fora e aparecem contados. Nada é distribuído por estimativa.</li>
        <li style={LI}><b>Valor</b> é o pago ao prestador, com a glosa já descontada pelo ERP. Coparticipação não é descontada.</li>
        <li style={LI}><b>Período:</b> a internação pertence ao mês em que começou (primeira data de atendimento da guia), não ao mês em que a conta foi apresentada.</li>
      </ul>

      <div style={H}>3. Qual é "a cirurgia" de cada internação</div>
      <p style={P}>
        O <b>procedimento principal</b> é o procedimento cirúrgico (código da tabela que começa com 3) de maior valor dentro da internação.
        Exames (códigos 4…) e consultas não contam, mesmo quando são caros. Internação sem nenhum procedimento cirúrgico é <b>clínica</b>.
      </p>

      <div style={H}>4. Como a comparação é feita</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}>Só entra a cirurgia feita nos dois lados com <b>pelo menos {fmtN(minCasos)} internações em cada lado</b>. Com menos, ela fica listada em "poucos casos", fora do total.</li>
        <li style={LI}>O custo típico de cada lado é a <b>mediana</b>: o valor do meio. Metade das internações custou menos e metade custou mais. Uma internação extrema não arrasta a mediana como arrastaria a média; a média também aparece, para comparação.</li>
        <li style={LI}>O total aplica, ao número de internações do primeiro lado, a mediana de cada lado: <b>internações de {ra} × (mediana de {rb} − mediana de {ra})</b>, somado em todas as cirurgias comparáveis.</li>
        <li style={LI}>Internações clínicas são comparadas à parte, pelo custo por diária do paciente (diárias de acompanhante não contam).</li>
      </ul>
      {ex && (
        <div style={{ background: T.tealSoft, borderRadius: 8, padding: "10px 12px", margin: "8px 0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Exemplo real deste período: {ex.descricao} ({ex.principal})</div>
          <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.6, fontFamily: MONO }}>
            {ra}: {fmtN(ex.a.n)} internações, mediana {fmtR(ex.a.mediana)} (média {fmtR(ex.a.media)})<br />
            {rb}: {fmtN(ex.b.n)} internações, mediana {fmtR(ex.b.mediana)} (média {fmtR(ex.b.media)})<br />
            Peso no total: {fmtN(ex.a.n)} × ({fmtR(ex.b.mediana)} − {fmtR(ex.a.mediana)}) = <b>{fmtR(ex.peso)}</b>
          </div>
        </div>
      )}

      <div style={H}>5. O que esta conta não diz</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li style={LI}><b>Gravidade dentro da mesma cirurgia:</b> uma cesárea com complicação custa mais. A mediana reduz o efeito de casos extremos, mas não elimina diferença de perfil entre as redes. Abra as internações para ver os casos.</li>
        <li style={LI}><b>Itens fora da internação:</b> o que não foi ligado com segurança fica de fora; o quadro de montagem mostra quanto é.
          Quando itens da mesma pessoa, no mesmo hospital e nas mesmas datas ficam fora (por exemplo, a prótese lançada em conta ambulatorial), a cirurgia aparece em amarelo com <AlertTriangle size={11} style={{ verticalAlign: -1 }} />.
          Os valores não mudam; o detalhe mostra os itens e o painel "Pontos para esclarecer com o operador" reúne os casos mais claros.</li>
        <li style={LI}><b>Contas atrasadas:</b> internação recente pode estar incompleta; por isso o período sugerido termina 3 meses antes da última competência.</li>
        <li style={LI}><b>Preço não é tudo:</b> acesso, qualidade e preferência do beneficiário não estão aqui.</li>
      </ul>

      <div style={H}>6. Como conferir</div>
      <p style={P}>
        Clique numa cirurgia para ver as internações de cada lado; clique numa internação para ver a conta inteira, item por item, com data, prestador, código,
        quantidade e valor. Os itens ligados sem número de guia aparecem marcados. A soma dos itens é o total da internação.
      </p>
      {carga && <p style={{ ...P, color: T.inkSoft, fontSize: 12 }}>Última montagem: {dataHoraBR(carga.atualizado_em)}.</p>}
    </div>
  );
}

// ---------- a conta de uma internação ----------
function ContaInternacao({ guia, onFechar }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    let vivo = true;
    setD(null); setErro(null);
    api.get(`${BASE}/${encodeURIComponent(guia)}`).then((r) => vivo && setD(r.data))
      .catch((e) => vivo && setErro(erroDe(e, "Não foi possível abrir a internação.")));
    return () => { vivo = false; };
  }, [guia]);
  return (
    <div role="dialog" aria-modal="true" aria-label={`Conta da internação ${guia}`}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflow: "auto" }}
         onClick={onFechar}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 18, width: "min(1100px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <b style={{ fontSize: 15, color: T.ink }}>Conta da internação (guia {guia})</b>
          <div style={{ flex: 1 }} />
          <button style={btn(false)} onClick={onFechar} aria-label="Fechar"><X size={14} /> Fechar</button>
        </div>
        {erro && <div style={{ color: T.risco, fontSize: 13, marginTop: 10 }}>{erro}</div>}
        {!d && !erro && <div style={{ color: T.inkSoft, fontSize: 13, marginTop: 10 }}>Carregando a conta…</div>}
        {d && (() => {
          const c = d.internacao;
          const porNat = Object.keys(d.naturezas).map((k) => ({ k, rot: d.naturezas[k], vl: d.itens.filter((i) => i.natureza === k).reduce((s, i) => s + (i.valor_tot || 0), 0) }));
          const soma = d.itens.reduce((s, i) => s + (i.valor_tot || 0), 0);
          return (
            <>
              <div style={{ fontSize: 13, color: T.ink, marginTop: 8, lineHeight: 1.6 }}>
                {c.nm_prestador} · de {dataBR(c.ini)} a {dataBR(c.fim)} · {fmtN(c.diarias)} diária(s) do paciente ·
                {" "}{c.principal ? <>cirurgia principal: <b>{c.principal_desc}</b> ({c.principal})</> : "internação clínica"}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.ink, marginTop: 4 }}>{fmtR(c.vl_total)}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                {porNat.map((x) => (
                  <span key={x.k} style={{ fontSize: 12, padding: "3px 8px", borderRadius: 6, background: "#f5f7fa", borderLeft: `4px solid ${CORES_NAT[x.k]}` }}>
                    {x.rot}: <b>{fmtR(x.vl)}</b>
                  </span>
                ))}
              </div>
              {c.linhas_sem_guia > 0 && (
                <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>
                  {fmtN(c.linhas_sem_guia)} item(ns), somando {fmtR(c.vl_sem_guia)}, chegaram sem número de guia e foram ligados a esta internação pela regra (mesma pessoa, mesmo prestador, data dentro do período, e nenhuma outra internação possível). Estão marcados com "sem guia".
                </div>
              )}
              <div style={{ maxHeight: 460, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8, marginTop: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={TH}>Data</th><th style={TH}>Natureza</th><th style={TH}>Código</th><th style={TH}>Descrição</th>
                    <th style={TH}>Prestador</th><th style={{ ...TH, textAlign: "right" }}>Qtd.</th>
                    <th style={{ ...TH, textAlign: "right" }}>Valor pago</th><th style={{ ...TH, textAlign: "right" }}>Glosa</th><th style={TH} />
                  </tr></thead>
                  <tbody>
                    {d.itens.map((i, n) => (
                      <tr key={n}>
                        <td style={TD}>{dataBR(i.dt_realizado)}</td>
                        <td style={{ ...TD, fontSize: 12 }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: CORES_NAT[i.natureza], marginRight: 5 }} />{d.naturezas[i.natureza]}</td>
                        <td style={{ ...TD, fontFamily: MONO, fontSize: 12 }}>{i.procedimento}</td>
                        <td style={TD}>{i.descricao}</td>
                        <td style={{ ...TD, fontSize: 12 }}>{curto(i.nm_prestador, 28)}</td>
                        <td style={NUM}>{fmtN(i.qt_pago, Number.isInteger(i.qt_pago) ? 0 : 2)}</td>
                        <td style={NUM}>{fmtR(i.valor_tot)}</td>
                        <td style={NUM}>{i.vl_total_glosado ? fmtR(i.vl_total_glosado) : ""}</td>
                        <td style={{ ...TD, fontSize: 11, color: T.ambar }}>{i.sem_guia ? "sem guia" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>
                {fmtN(d.itens.length)} itens; soma {fmtR(soma)} (igual ao total da internação). Valor pago com a glosa já descontada pelo ERP; a coluna Glosa é informativa.
              </div>
              {d.fora?.length > 0 && (
                <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: c.pendencias?.length ? T.ambarSoft : "#f7f9fa", border: `1px solid ${c.pendencias?.length ? "#f5d38a" : T.line}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.pendencias?.length ? "#8a5a00" : T.ink, display: "flex", gap: 6, alignItems: "center" }}>
                    {c.pendencias?.length > 0 && <AlertTriangle size={15} />}
                    Itens da mesma pessoa no período que não estão nesta internação (não somados): {fmtR(d.fora.reduce((x, f) => x + (f.valor_tot || 0), 0))}
                  </div>
                  <div style={{ fontSize: 12, color: T.ink, margin: "4px 0 8px", lineHeight: 1.5 }}>
                    {d.regra_pendencia} {c.pendencias?.length > 0
                      ? "Esta internação foi marcada: confirme com o operador se estes itens são dela."
                      : "Abaixo do limite de marcação: listados só para conferência."}
                  </div>
                  <div style={{ maxHeight: 260, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={TH}>Data</th><th style={TH}>Motivo</th><th style={TH}>Descrição</th><th style={TH}>Conta</th><th style={TH}>Prestador</th><th style={{ ...TH, textAlign: "right" }}>Valor</th></tr></thead>
                      <tbody>
                        {d.fora.map((f, n) => (
                          <tr key={n}>
                            <td style={TD}>{dataBR(f.dt_realizado)}</td>
                            <td style={{ ...TD, fontSize: 11 }}>{(d.motivos[f.motivo] || f.motivo).split(" (")[0]}</td>
                            <td style={TD}>{f.descricao} <span style={{ color: T.inkSoft, fontSize: 11 }}>{f.procedimento}</span></td>
                            <td style={{ ...TD, fontSize: 11 }}>{f.tp_conta === "A" ? "ambulatorial" : f.tp_conta === "I" ? "internação" : f.tp_conta}{f.sem_guia ? ", sem guia" : `, guia ${f.nr_guia}`}</td>
                            <td style={{ ...TD, fontSize: 11 }}>{curto(f.nm_prestador, 26)}</td>
                            <td style={NUM}>{fmtR(f.valor_tot)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ---------- internações de um lado ----------
function ListaLado({ params, lado, rotulo, principal, clinicas, onAbrir }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    setD(null);
    api.get(`${BASE}/lista`, { params: { mes: params.mes, mes_ate: params.mes_ate, tp: params.tp, lado, principal: principal || undefined, clinicas: clinicas || undefined, limite: 100 } })
      .then((r) => vivo && setD(r.data)).catch(() => vivo && setD({ internacoes: [] }));
    return () => { vivo = false; };
  }, [params, lado, principal, clinicas]);
  return (
    <div style={{ flex: 1, minWidth: 340 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, marginBottom: 4 }}>{rotulo}</div>
      {!d ? <div style={{ fontSize: 12, color: T.inkSoft }}>Carregando…</div> : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={TH}>Hospital</th><th style={TH}>Início</th><th style={{ ...TH, textAlign: "right" }}>Diárias</th><th style={{ ...TH, textAlign: "right" }}>Total</th></tr></thead>
          <tbody>
            {d.internacoes.map((i) => (
              <tr key={i.guia} onClick={() => onAbrir(i.guia)} onKeyDown={(e) => e.key === "Enter" && onAbrir(i.guia)} tabIndex={0} style={{ cursor: "pointer" }} title="Abrir a conta item por item">
                <td style={{ ...TD, fontSize: 12 }}>{i.pendencias?.length > 0 && <AlertTriangle size={12} color={T.ambar} style={{ marginRight: 4, verticalAlign: -1 }} aria-label="conta possivelmente incompleta" />}{curto(i.nm_prestador, 30)}</td>
                <td style={{ ...TD, fontSize: 12 }}>{dataBR(i.ini)}</td>
                <td style={NUM}>{fmtN(i.diarias)}</td>
                <td style={{ ...NUM, color: T.teal, textDecoration: "underline" }} title={i.pendencias?.length ? `${fmtR(i.vl_fora)} da mesma pessoa no período ficaram fora desta internação` : undefined}>{fmtR(i.vl_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- pontos para esclarecer com o operador ----------
function textoOperador(d, periodo) {
  const linhas = [
    `Pontos para esclarecer: contas de internação possivelmente incompletas (${periodo}).`,
    "",
    "Ao montar o custo da internação inteira pela guia, encontramos itens da mesma pessoa, no mesmo hospital e nas mesmas datas, lançados fora da guia (em geral em conta ambulatorial e sem número de guia). Precisamos entender se é ajuste de lançamento ou se há uma regra de faturamento que devemos aplicar.",
    "",
    ...d.por_rede.filter((r) => r.internacoes > 0).map((r) => `- ${r.rotulo}: ${r.com_pendencia} de ${r.internacoes} internações nessa situação (R$ ${Math.round(r.vl_fora).toLocaleString("pt-BR")} fora da guia).`),
    "",
    "Exemplos:",
  ];
  d.casos.forEach((c, n) => {
    linhas.push(`${n + 1}. Guia ${c.guia}, ${c.nm_prestador}, ${dataBR(c.ini)} a ${dataBR(c.fim)}, ${c.principal_desc || "internação clínica"}: conta da guia R$ ${Math.round(c.vl_total).toLocaleString("pt-BR")}; fora da guia R$ ${Math.round(c.vl_fora).toLocaleString("pt-BR")}.`);
    c.itens.slice(0, 3).forEach((f) => linhas.push(`   - ${f.descricao} (${f.procedimento}), R$ ${Math.round(f.valor_tot).toLocaleString("pt-BR")}, ${dataBR(f.dt_realizado)}, conta ${f.tp_conta === "A" ? "ambulatorial" : f.tp_conta === "I" ? "de internação" : f.tp_conta}${f.sem_guia ? " sem guia" : ""}.`));
  });
  linhas.push("", "Pergunta: esses itens pertencem à internação da guia indicada? Se sim, por que vêm em outra conta e sem a guia?");
  return linhas.join("\n");
}

function PontosOperador({ params, onAbrir }) {
  const [aberto, setAberto] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!aberto) return undefined;
    let vivo = true;
    setD(null);
    api.get(`${BASE}/pendencias`, { params: { ...params, limite: 10 } }).then((r) => vivo && setD(r.data)).catch(() => vivo && setD({ por_rede: [], casos: [] }));
    return () => { vivo = false; };
  }, [aberto, params]);
  const periodo = params.mes_ate ? `${mesBR(params.mes)} a ${mesBR(params.mes_ate)}` : mesBR(params.mes);
  const copiar = async () => {
    try { await navigator.clipboard.writeText(textoOperador(d, periodo)); toast.success("Texto copiado"); }
    catch { toast.error("Não foi possível copiar"); }
  };
  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
      <button style={btn(aberto)} onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
        {aberto ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <AlertTriangle size={13} color={T.ambar} /> Pontos para esclarecer com o operador
      </button> <InfoTip w={340} texto={TIP.operador} />
      {aberto && !d && <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 8 }}>Carregando…</div>}
      {aberto && d && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: T.ink, lineHeight: 1.6 }}>
            {d.por_rede.filter((r) => r.internacoes > 0).map((r) => (
              <div key={r.rede}><b>{r.rotulo}:</b> {fmtN(r.com_pendencia)} de {fmtN(r.internacoes)} internações com conta possivelmente incompleta ({fmtR0(r.vl_fora)} fora da guia).</div>
            ))}
            <div style={{ color: T.inkSoft, marginTop: 4 }}>{d.regra}</div>
          </div>
          {d.casos.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 6px" }}>
                <b style={{ fontSize: 12, color: T.ink }}>Os {fmtN(d.casos.length)} casos mais claros</b>
                <button style={btn(false)} onClick={copiar}><Copy size={13} /> Copiar texto para o operador</button>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {d.casos.map((c) => (
                  <div key={c.guia} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #f5d38a", background: T.ambarSoft }}>
                    <div style={{ fontSize: 13, color: T.ink }}>
                      <b>{c.principal_desc || "Internação clínica"}</b> · {c.nm_prestador} ({c.rotulo_rede}) · {dataBR(c.ini)} a {dataBR(c.fim)}
                    </div>
                    <div style={{ fontSize: 12, color: T.ink, marginTop: 2 }}>
                      Conta da guia: <b>{fmtR(c.vl_total)}</b> · fora da guia: <b style={{ color: "#8a5a00" }}>{fmtR(c.vl_fora)}</b>
                      {" "}<button onClick={() => onAbrir(c.guia)} style={{ ...btn(false), padding: "2px 8px", marginLeft: 6 }}>abrir a conta</button>
                    </div>
                    <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, color: T.inkSoft }}>
                      {c.itens.slice(0, 3).map((f, n) => (
                        <li key={n}>{f.descricao} ({f.procedimento}): {fmtR(f.valor_tot)} em {dataBR(f.dt_realizado)}, conta {f.tp_conta === "A" ? "ambulatorial" : f.tp_conta === "I" ? "de internação" : f.tp_conta}{f.sem_guia ? " sem guia" : ""}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- a subtela ----------
export default function Internacoes({ tp }) {
  const [mes, setMes] = useState("");
  const [mesAte, setMesAte] = useState("");
  const [padrao, setPadrao] = useState(null);
  const [ladoA, setLadoA] = useState("rede:DIRETA");
  const [ladoB, setLadoB] = useState("rede:INTERCAMBIO");
  const [minCasos, setMinCasos] = useState(5);
  const [categorias, setCategorias] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [comp, setComp] = useState(null);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [verPoucos, setVerPoucos] = useState(false);
  const [verClinicas, setVerClinicas] = useState(false);
  const [verMetodo, setVerMetodo] = useState(false);
  const [verTecnico, setVerTecnico] = useState(false);
  const [conta, setConta] = useState(null);

  useEffect(() => {
    api.get(`${BASE}/periodo`).then((r) => {
      setPadrao(r.data);
      if (r.data.mes) { setMes(r.data.mes); setMesAte(r.data.mes_ate); }
    }).catch(() => setPadrao({}));
    api.get("/resultado-plano/utilizacao/rede/regras").then((r) => setCategorias(r.data.categorias)).catch(() => {});
  }, []);

  const params = useMemo(() => ({ mes, mes_ate: mesAte && mesAte > mes ? mesAte : undefined, tp: tp || undefined }), [mes, mesAte, tp]);

  useEffect(() => {
    if (!mes) return undefined;
    let vivo = true;
    setResumo(null); setErro(null);
    api.get(`${BASE}/resumo`, { params }).then((r) => vivo && setResumo(r.data))
      .catch((e) => vivo && setErro(erroDe(e, "Não foi possível carregar as internações.")));
    return () => { vivo = false; };
  }, [params, mes]);

  useEffect(() => {
    if (!mes || ladoA === ladoB) { setComp(null); return undefined; }
    let vivo = true;
    setComp(null);
    api.get(`${BASE}/comparativo`, { params: { ...params, lado_a: ladoA, lado_b: ladoB, min_casos: minCasos } })
      .then((r) => vivo && setComp(r.data)).catch((e) => vivo && setErro(erroDe(e, "Não foi possível comparar.")));
    return () => { vivo = false; };
  }, [params, mes, ladoA, ladoB, minCasos]);

  const carga = resumo?.carga || padrao?.carga;
  if (padrao && !padrao.mes) {
    return (
      <div style={card}>
        <b style={{ fontSize: 15, color: T.ink }}>Internações</b>
        <p style={{ fontSize: 13, color: T.inkSoft, marginTop: 8 }}>
          As internações aparecem depois da próxima sincronização das contas médicas: o número da guia, que junta os itens de uma mesma internação,
          passou a ser copiado do ERP nesta versão. A sincronização roda todos os dias; o gestor também pode disparar em Resultado por Plano.
        </p>
      </div>
    );
  }
  const ra = comp?.rotulo_a || "Primeiro lado", rb = comp?.rotulo_b || "Segundo lado";
  const ag = comp?.agregado, cob = comp?.cobertura;
  const graf = (comp?.comparaveis || []).slice(0, 10).map((i) => ({ nome: curto(i.descricao, 34), a: i.a.mediana, b: i.b.mediana }));
  const compRede = (resumo?.redes || []).filter((r) => r.todas?.n).map((r) => ({
    rotulo: r.rotulo, ...Object.fromEntries(Object.entries(r.todas.composicao).map(([k, v]) => [k, v || 0])),
  }));

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: T.ink }}>Internações: quanto custa a conta inteira em cada rede</b>
        <InfoTip w={420} texto={TIP.tela} />
        <div style={{ flex: 1 }} />
        <button style={btn(verMetodo)} onClick={() => setVerMetodo((v) => !v)} aria-expanded={verMetodo}>
          {verMetodo ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Como esta tela calcula (passo a passo)
        </button>
      </div>
      {verMetodo && <Metodologia comp={comp} carga={carga} minCasos={minCasos} />}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
          <span>Internações iniciadas de <InfoTip w={320} texto={TIP.periodo} /></span>
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} style={campo} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
          até
          <input type="month" value={mesAte} min={mes} onChange={(e) => setMesAte(e.target.value)} style={campo} />
        </label>
        <SeletorLado rotulo="Primeiro lado" valor={ladoA} onChange={setLadoA} categorias={categorias} prestadores={[]} />
        <SeletorLado rotulo="Comparado com" valor={ladoB} onChange={setLadoB} categorias={categorias} prestadores={[]} />
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
          <span>Mínimo de casos por lado <InfoTip w={280} texto={TIP.minimo} /></span>
          <input type="number" min={1} value={minCasos} onChange={(e) => setMinCasos(Math.max(1, Number(e.target.value) || 1))} style={{ ...campo, width: 90 }} />
        </label>
      </div>
      {padrao?.ultima_competencia && (
        <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>
          Período sugerido: {mesBR(padrao.mes)} a {mesBR(padrao.mes_ate)} (última competência carregada: {mesBR(padrao.ultima_competencia)}; os 3 meses mais recentes ficam de fora porque as contas ainda estão chegando).
        </div>
      )}
      {ladoA === ladoB && <div style={{ marginTop: 8, fontSize: 12, color: T.risco }}>Os dois lados são o mesmo: escolha lados diferentes.</div>}
      {erro && <div style={{ marginTop: 10, color: T.risco, fontSize: 13 }}>{erro}</div>}

      {/* como foram montadas */}
      {carga && (
        <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "#f7f9fa", fontSize: 12, color: T.ink, lineHeight: 1.6 }}>
          <b>Como as internações foram montadas</b> <InfoTip w={320} texto={TIP.carga} /> (todas as internações carregadas, não só o período):
          {" "}{fmtN(carga.internacoes)} internações; {fmtN(carga.linhas_sem_guia)} itens chegaram sem número de guia ({fmtR0(carga.vl_sem_guia)}):
          {" "}<b>{fmtN(carga.linhas_ligadas)}</b> ligados a uma internação ({fmtR0(carga.vl_ligado)}),
          {" "}{fmtN(carga.linhas_ambiguas)} ambíguos ({fmtR0(carga.vl_ambiguo)}) e o restante sem internação correspondente ({fmtR0(carga.vl_nao_ligado)}) ficaram de fora.
          {" "}Fora também: {fmtN(carga.guias_sem_diaria)} guias sem diária e {fmtN(carga.guias_varias_pessoas)} guias com mais de uma pessoa.
        </div>
      )}

      {/* panorama por rede */}
      {resumo && resumo.redes.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={titulo}>Panorama por rede do hospital (ainda não é comparação justa) <InfoTip w={340} texto={TIP.resumo} /></div>
          <div style={{ overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={TH}>Rede</th><th style={{ ...TH, textAlign: "right" }}>Internações</th>
                <th style={{ ...TH, textAlign: "right" }}>Cirúrgicas / clínicas</th>
                <th style={{ ...TH, textAlign: "right" }}>Mediana <InfoTip w={280} texto={TIP.mediana} /></th>
                <th style={{ ...TH, textAlign: "right" }}>Média</th>
                <th style={{ ...TH, textAlign: "right" }}>Faixa do meio <InfoTip w={280} texto={TIP.faixa} /></th>
                <th style={{ ...TH, textAlign: "right" }}>Custo por diária</th>
                <th style={{ ...TH, textAlign: "right" }}>Valor total</th>
                <th style={{ ...TH, textAlign: "right" }}>Cobertura <InfoTip w={340} texto={TIP.cobertura} /></th>
              </tr></thead>
              <tbody>
                {resumo.redes.filter((r) => r.todas?.n).map((r) => (
                  <tr key={r.rede}>
                    <td style={TD}>{r.rotulo}</td>
                    <td style={NUM}>{fmtN(r.todas.n)}</td>
                    <td style={NUM}>{fmtN(r.cirurgicas?.n || 0)} / {fmtN(r.clinicas?.n || 0)}</td>
                    <td style={NUM}>{fmtR0(r.todas.mediana)}</td>
                    <td style={NUM}>{fmtR0(r.todas.media)}</td>
                    <td style={NUM}>{fmtR0(r.todas.p25)} a {fmtR0(r.todas.p75)}</td>
                    <td style={NUM}>{fmtR0(r.todas.custo_por_diaria)}</td>
                    <td style={NUM}>{fmtK(r.todas.vl_total)}</td>
                    <td style={{ ...NUM, color: !vazio(r.pct_cobertura) && r.pct_cobertura < 80 ? T.ambar : T.ink }}>{fmtP(r.pct_cobertura)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const cobs = resumo.redes.filter((r) => r.todas?.n >= 10 && !vazio(r.pct_cobertura));
            if (cobs.length < 2) return null;
            const min = Math.min(...cobs.map((r) => r.pct_cobertura)), max = Math.max(...cobs.map((r) => r.pct_cobertura));
            if (max - min < 10) return null;
            return (
              <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: T.ambarSoft, color: "#8a5a00", fontSize: 12, display: "flex", gap: 8 }}>
                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  A cobertura é desigual entre as redes ({cobs.map((r) => `${r.rotulo}: ${fmtP(r.pct_cobertura)}`).join("; ")}).
                  Parte da conta hospitalar chega do ERP sem número de guia e sem dado que permita ligar com segurança à internação.
                  Enquanto isso não for resolvido na origem, a rede com menor cobertura aparece mais barata do que é; leia as diferenças com esse limite.
                </span>
              </div>
            );
          })()}
          {compRede.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={titulo}>De que é feita a conta, por rede <InfoTip w={340} texto={TIP.composicao} /></div>
              <ResponsiveContainer width="100%" height={Math.max(110, compRede.length * 34 + 50)}>
                <BarChart layout="vertical" data={compRede} margin={{ top: 4, right: 16, left: 8, bottom: 4 }} stackOffset="expand">
                  <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="rotulo" width={200} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v, n) => [fmtP(v), resumo.naturezas[n]]} />
                  <Legend formatter={(n) => resumo.naturezas[n]} wrapperStyle={{ fontSize: 11 }} />
                  {Object.keys(resumo.naturezas).map((k) => <Bar key={k} dataKey={k} stackId="c" fill={CORES_NAT[k]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
      {resumo && resumo.redes.length === 0 && <div style={{ marginTop: 12, fontSize: 13, color: T.inkSoft }}>Nenhuma internação iniciada no período.</div>}

      {/* comparação justa */}
      {comp && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
          <div style={titulo}>Mesma cirurgia nos dois lados: {ra} x {rb} <InfoTip w={360} texto={TIP.comparativo} /></div>
          <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${T.line}`, background: "#fafbfc" }}>
            {comp.comparaveis.length === 0 ? (
              <div style={{ fontSize: 13, color: T.inkSoft }}>
                Nenhuma cirurgia com pelo menos {fmtN(minCasos)} internações em cada lado no período. Aumente o período ou reduza o mínimo de casos
                ({fmtN(comp.poucos_casos.length)} cirurgia(s) aparecem nos dois lados com menos casos).
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.6 }}>
                  Nas <b>{fmtN(comp.comparaveis.length)} cirurgias</b> comparáveis, {ra} teve <b>{fmtN(ag.internacoes_a)} internações</b>.
                  Pela mediana de {ra} elas custam <b>{fmtR(ag.a_mediana_a)}</b>; pela mediana de {rb} da mesma cirurgia custariam <b>{fmtR(ag.a_mediana_b)}</b>.
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 6, color: ag.diferenca >= 0 ? T.verde : T.risco }}>
                  {ag.diferenca >= 0 ? `${ra} sai ${fmtR(ag.diferenca)} mais barato` : `${ra} sai ${fmtR(-ag.diferenca)} mais caro`}
                </div>
                <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 6 }}>
                  Cobertura: {fmtP(cob.pct_a)} das internações cirúrgicas de {ra} ({fmtN(cob.comparaveis_a)} de {fmtN(cob.cirurgicas_a)}) e {fmtP(cob.pct_b)} das de {rb} ({fmtN(cob.comparaveis_b)} de {fmtN(cob.cirurgicas_b)}) entraram na comparação.
                  {" "}{fmtN(comp.so_um_lado.a)} cirurgia(s) só aparecem em {ra} e {fmtN(comp.so_um_lado.b)} só em {rb}.
                </div>
              </>
            )}
          </div>

          {graf.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={titulo}>Mediana da internação por cirurgia (as mais frequentes)</div>
              <ResponsiveContainer width="100%" height={Math.max(180, graf.length * 34 + 40)}>
                <BarChart layout="vertical" data={graf} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                  <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="nome" width={240} tick={{ fontSize: 10 }} interval={0} />
                  <RTooltip formatter={(v) => fmtR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="a" name={ra} fill="#20a490" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="b" name={rb} fill={T.azul} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {comp.comparaveis.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 520, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={TH}>Cirurgia (procedimento principal)</th>
                  <th style={{ ...TH, textAlign: "right" }}>Internações {ra}</th><th style={{ ...TH, textAlign: "right" }}>Mediana {ra}</th>
                  <th style={{ ...TH, textAlign: "right" }}>Internações {rb}</th><th style={{ ...TH, textAlign: "right" }}>Mediana {rb}</th>
                  <th style={{ ...TH, textAlign: "right" }}>Diferença</th>
                </tr></thead>
                <tbody>
                  {comp.comparaveis.map((i) => (
                    <React.Fragment key={i.principal}>
                      <tr onClick={() => setAberto(aberto === i.principal ? null : i.principal)} onKeyDown={(e) => e.key === "Enter" && setAberto(aberto === i.principal ? null : i.principal)}
                          tabIndex={0} aria-expanded={aberto === i.principal} style={{ cursor: "pointer", background: i.pendencia ? T.ambarSoft : undefined }}>
                        <td style={TD}>{aberto === i.principal ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {i.pendencia && <AlertTriangle size={13} color={T.ambar} style={{ margin: "0 4px", verticalAlign: -2 }} aria-label="conta possivelmente incompleta" />}
                          {" "}{i.descricao} <span style={{ color: T.inkSoft, fontSize: 11 }}>{i.principal}</span></td>
                        <td style={NUM}>{fmtN(i.a.n)}</td>
                        <td style={NUM} title={`média ${fmtR(i.a.media)}; faixa do meio ${fmtR0(i.a.p25)} a ${fmtR0(i.a.p75)}`}>{fmtR0(i.a.mediana)}</td>
                        <td style={NUM}>{fmtN(i.b.n)}</td>
                        <td style={NUM} title={`média ${fmtR(i.b.media)}; faixa do meio ${fmtR0(i.b.p25)} a ${fmtR0(i.b.p75)}`}>{fmtR0(i.b.mediana)}</td>
                        <td style={{ ...NUM, color: vazio(i.dif_mediana_pct) ? T.inkSoft : i.dif_mediana_pct > 0 ? T.risco : T.verde }}>
                          {vazio(i.dif_mediana_pct) ? "—" : `${i.dif_mediana_pct > 0 ? "+" : ""}${fmtP(i.dif_mediana_pct)}`}
                        </td>
                      </tr>
                      {aberto === i.principal && (
                        <tr><td colSpan={6} style={{ padding: 10, background: "#fafbfc" }}>
                          {i.pendencia && (
                            <div style={{ marginBottom: 8, padding: "8px 12px", borderRadius: 8, background: T.ambarSoft, color: "#8a5a00", fontSize: 12, lineHeight: 1.5, display: "flex", gap: 8 }}>
                              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                              <span>
                                <b>Atenção: conta possivelmente incompleta.</b>{" "}
                                {[["a", ra], ["b", rb]].filter(([k]) => i[k].n_pendencia > 0).map(([k, rot]) =>
                                  `${rot}: ${fmtN(i[k].n_pendencia)} de ${fmtN(i[k].n)} internação(ões) com ${fmtR(i[k].vl_fora_pendencia)} da mesma pessoa, no mesmo período, fora da guia`).join("; ")}.
                                {" "}Esses itens (muitas vezes a prótese ou o pacote, lançados em conta ambulatorial) não entram na mediana, então o lado afetado pode parecer mais barato do que é.
                                Os valores acima não foram alterados. Abra as internações marcadas com <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> para ver os itens.
                              </span>
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 6 }}>
                            {ra}: média {fmtR(i.a.media)}, faixa do meio {fmtR0(i.a.p25)} a {fmtR0(i.a.p75)}, de {fmtR0(i.a.minimo)} a {fmtR0(i.a.maximo)}.
                            {" "}{rb}: média {fmtR(i.b.media)}, faixa do meio {fmtR0(i.b.p25)} a {fmtR0(i.b.p75)}, de {fmtR0(i.b.minimo)} a {fmtR0(i.b.maximo)}.
                            {" "}Clique numa internação para ver a conta inteira.
                          </div>
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <ListaLado params={params} lado={comp.lado_a} rotulo={ra} principal={i.principal} onAbrir={setConta} />
                            <ListaLado params={params} lado={comp.lado_b} rotulo={rb} principal={i.principal} onAbrir={setConta} />
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>Diferença positiva: a mediana de {ra} é mais alta. Passe o mouse na mediana para ver média e faixa.</div>

          {comp.poucos_casos.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button style={btn(verPoucos)} onClick={() => setVerPoucos((v) => !v)}>
                {verPoucos ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {fmtN(comp.poucos_casos.length)} cirurgia(s) nos dois lados com menos de {fmtN(minCasos)} casos (fora do total)
              </button>
              {verPoucos && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                  <thead><tr><th style={TH}>Cirurgia</th><th style={{ ...TH, textAlign: "right" }}>Internações {ra}</th><th style={{ ...TH, textAlign: "right" }}>Mediana {ra}</th><th style={{ ...TH, textAlign: "right" }}>Internações {rb}</th><th style={{ ...TH, textAlign: "right" }}>Mediana {rb}</th></tr></thead>
                  <tbody>{comp.poucos_casos.slice(0, 80).map((i) => (
                    <tr key={i.principal} style={{ background: i.pendencia ? T.ambarSoft : undefined }}><td style={TD}>{i.pendencia && <AlertTriangle size={12} color={T.ambar} style={{ marginRight: 4, verticalAlign: -1 }} aria-label="conta possivelmente incompleta" />}{i.descricao} <span style={{ color: T.inkSoft, fontSize: 11 }}>{i.principal}</span></td>
                      <td style={NUM}>{fmtN(i.a.n)}</td><td style={NUM}>{fmtR0(i.a.mediana)}</td><td style={NUM}>{fmtN(i.b.n)}</td><td style={NUM}>{fmtR0(i.b.mediana)}</td></tr>))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <button style={btn(verClinicas)} onClick={() => setVerClinicas((v) => !v)}>
              {verClinicas ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Internações clínicas (sem cirurgia)
            </button> <InfoTip w={320} texto={TIP.clinicas} />
            {verClinicas && (
              <div style={{ marginTop: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={TH}>Lado</th><th style={{ ...TH, textAlign: "right" }}>Internações</th><th style={{ ...TH, textAlign: "right" }}>Diárias</th>
                    <th style={{ ...TH, textAlign: "right" }}>Custo por diária</th><th style={{ ...TH, textAlign: "right" }}>Mediana da internação</th><th style={{ ...TH, textAlign: "right" }}>Mediana de diárias</th></tr></thead>
                  <tbody>
                    {[["a", ra], ["b", rb]].map(([k, rot]) => { const c = comp.clinicas[k]; return (
                      <tr key={k}><td style={TD}>{rot}</td><td style={NUM}>{fmtN(c.n)}</td><td style={NUM}>{fmtN(c.diarias)}</td>
                        <td style={NUM}>{fmtR0(c.custo_por_diaria)}</td><td style={NUM}>{fmtR0(c.mediana)}</td><td style={NUM}>{fmtN(c.mediana_diarias, 1)}</td></tr>); })}
                  </tbody>
                </table>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
                  <ListaLado params={params} lado={comp.lado_a} rotulo={`${ra} (clínicas)`} clinicas onAbrir={setConta} />
                  <ListaLado params={params} lado={comp.lado_b} rotulo={`${rb} (clínicas)`} clinicas onAbrir={setConta} />
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: T.inkSoft, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
            <b style={{ color: T.ink }}>De onde vêm estes números</b>
            <ul style={{ margin: "6px 0 0 18px", padding: 0, lineHeight: 1.6 }}>
              <li>{comp.procedencia.texto.periodo}; {comp.procedencia.texto.filtros}.</li>
              {comp.procedencia.texto.regras.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <button style={{ ...btn(verTecnico), marginTop: 6 }} onClick={() => setVerTecnico((v) => !v)}>{verTecnico ? "Esconder" : "Ver"} detalhe técnico (para auditoria)</button>
            {verTecnico && (
              <div style={{ fontFamily: MONO, fontSize: 11, marginTop: 6 }}>
                {Object.entries(comp.procedencia.tecnico).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
              </div>
            )}
          </div>
        </div>
      )}
      {!comp && mes && ladoA !== ladoB && !erro && <div style={{ marginTop: 12, fontSize: 13, color: T.inkSoft }}>Comparando…</div>}
      {carga && carga.vl_nao_ligado > 0 && (
        <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: T.ambarSoft, color: "#8a5a00", fontSize: 12, display: "flex", gap: 8 }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Itens de conta hospitalar sem número de guia que não puderam ser ligados com segurança a uma internação ({fmtR0(carga.vl_nao_ligado + carga.vl_ambiguo)} em toda a base carregada) não entram em nenhuma internação. Se a maior parte for de um lado, aquele lado aparece mais barato do que é.</span>
        </div>
      )}
      {mes && <PontosOperador params={params} onAbrir={setConta} />}
      {conta && <ContaInternacao guia={conta} onFechar={() => setConta(null)} />}
    </div>
  );
}
